// Mobile-only sticky bottom payment bar. Visible on <lg screens only.
// Collapsed: shows item count + total + THANH TOÁN button.
// Expanded: slides up ~60vh and renders the full PaymentPanel.
import { useState, useEffect } from "react";
import { PaymentPanel, type PaymentPanelProps } from "./payment-panel";

function fmtVND(n: number) {
  return new Intl.NumberFormat("vi-VN").format(Math.round(n)) + " ₫";
}

interface MobilePaymentBarProps extends PaymentPanelProps {
  itemCount: number;
}

export function MobilePaymentBar({ itemCount, ...panelProps }: MobilePaymentBarProps) {
  const [expanded, setExpanded] = useState(false);

  // Compute total the same way PaymentPanel does.
  const subtotal = parseFloat(panelProps.order?.subtotal ?? "0");
  const discountAmount = parseFloat(panelProps.order?.discountAmount ?? "0");
  const couponAmount = panelProps.coupon?.amountOff ?? 0;
  const total = Math.max(0, subtotal - discountAmount - couponAmount);

  // Auto-expand when view changes to cash or qr.
  useEffect(() => {
    if (panelProps.view !== "summary") setExpanded(true);
  }, [panelProps.view]);

  // Auto-collapse when payment completes (view back to summary after being on cash/qr).
  // We rely on the parent resetting view to "summary" on payment success.

  return (
    <div className="fixed bottom-0 inset-x-0 lg:hidden z-30">
      {/* Backdrop — click outside to collapse */}
      {expanded && (
        <div
          className="fixed inset-0 bg-black/30"
          onClick={() => setExpanded(false)}
        />
      )}

      {/* Slide-up panel container */}
      <div
        className={[
          "relative bg-card border-t shadow-lg transition-[height] duration-300 ease-out overflow-hidden",
          expanded ? "h-[60vh]" : "h-14",
        ].join(" ")}
      >
        {expanded ? (
          // Expanded: drag handle + scrollable PaymentPanel
          <div className="flex flex-col h-full">
            {/* Drag handle */}
            <div
              className="flex justify-center py-2 shrink-0 cursor-pointer"
              onClick={() => setExpanded(false)}
            >
              <div className="w-10 h-1 rounded-full bg-muted-foreground/30" />
            </div>
            {/* Scrollable panel content */}
            <div className="flex-1 overflow-y-auto">
              <PaymentPanel {...panelProps} />
            </div>
          </div>
        ) : (
          // Collapsed: summary bar
          <button
            onClick={() => setExpanded(true)}
            className="flex items-center justify-between w-full h-14 px-4"
            aria-label="Mở thanh toán"
          >
            <span className="text-sm text-muted-foreground">
              {itemCount} SP{" "}
              <span className="text-muted-foreground/60">|</span>{" "}
              TỔNG:{" "}
              <span className="font-semibold text-foreground">{fmtVND(total)}</span>
            </span>
            <span className="bg-primary text-primary-foreground rounded-md px-4 py-1.5 text-sm font-semibold">
              THANH TOÁN
            </span>
          </button>
        )}
      </div>
    </div>
  );
}
